import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IBooking, ISectionItem, ITableItem } from 'shared-types/SharedTypes';
import LayoutsService from 'external-services/layouts-service/layouts.service';
import { IActiveSectionsState } from './types';
import { BookingCreatorState } from 'booking-creator/rootReducer';
import { IDiaryService } from 'shared-types/DiaryTypes';
import TablesLookupService from 'external-services/tables-lookup-service/tablesLookup.service';
import {
  IBookingsByTable,
  IBookingSubset,
} from 'external-services/booking-availability-service/types';
import { BookingAvailabilityService } from 'external-services/booking-availability-service';
import { DiaryRootState } from 'appRoot/rootReducer';
import { setBookingsForSchedule } from 'booking-creator/reducers/activeBookings/slice';
import { BookingMode } from 'booking-creator/reducers/display/types';
import { sortBy } from 'lodash-es';

const NS = 'activeSectionsSlice';

const initialState: IActiveSectionsState = {
  tablesListForActiveService: [],
  // selectedTablesList: [],
  selectedTableIds: [],
  activeSections: [],
  bookingsByTable: {},
};

export const setSectionsAndTablesForActiveService = createAsyncThunk(
  'activeSections/setSectionsAndTablesForActiveService',
  async (
    // cancelCB can be passed if you need to cancel execution midway through the function
    payload: {cancelCB?: (calleeID?: string) => boolean},
    { dispatch, getState }
  ): Promise<{
    tablesList: ITableItem[];
    sections: ISectionItem[];
    bookingsByTable: IBookingsByTable;
  }> => {
    const {
      displayReducer: { activeBooking, selectedService, mode },
      scheduleReducer: { selectedDate }
    } = getState() as DiaryRootState;

    // omits any layouts that are not in use (which would have a null layoutId)
    const layoutIds: string[] = LayoutsService.getLayoutIds(selectedService as IDiaryService);
    const dateTime: Date = activeBooking.time || selectedDate;

    try {
      const { tablesList, sections } = await TablesLookupService.getTablesByLayoutIds(
        layoutIds,
        dateTime,
        activeBooking.preferredSectionId
      );

      if (payload.cancelCB && payload.cancelCB(`setSectionsAndTablesForActiveService-1`)) return null;

      await dispatch(setBookingsForSchedule());

      if (payload.cancelCB && payload.cancelCB(`setSectionsAndTablesForActiveService-2`)) return null;

      const {
        activeBookingsReducer: { activeScheduleBookings },
      } = getState() as BookingCreatorState;

      let bookingsByTable: IBookingsByTable;

      if (mode === BookingMode.editBooking) {
        // create a bookings by table without the current booking so it is a valid booking
        const bookings = [...activeScheduleBookings];
        const index = bookings.findIndex((b) => b._id === activeBooking._id);
        if (index > -1) {
          // remove booking
          bookings.splice(index, 1);
        }
        bookingsByTable = await getBookingsByTable(bookings);
      } else {
        bookingsByTable = await getBookingsByTable(activeScheduleBookings);
      }

      if (payload.cancelCB && payload.cancelCB(`setSectionsAndTablesForActiveService-3`)) return null;

      // sorts the tables by name (numerical/alphabetical). When they get to the table seater they will also be sorted by section.
      const tablesListSorted: ITableItem[] = tablesList?.length
        ? sortBy(tablesList, (t) => parseInt(t.name))
        : [];

      return { tablesList: tablesListSorted, sections, bookingsByTable };
    } catch (err) {
      console.warn(
        NS,
        `Error on 'setSectionsAndTablesForActiveService' getting tables and sections. selectedService, date: `,
        selectedService,
        dateTime,
        err
      );
      return null;
    }
  }
);

const activeSectionsSlice = createSlice({
  name: 'activeSections', // used in action types
  initialState,
  reducers: {
    reset: () => {
      return initialState;
    },
    setSelectedTableIds: (state: IActiveSectionsState, action: PayloadAction<string[]>) => {
      state.selectedTableIds = action.payload;
    },
    setTablesListForActiveService: (
      state: IActiveSectionsState,
      action: PayloadAction<ITableItem[]>
    ) => {
      state.tablesListForActiveService = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setSectionsAndTablesForActiveService.fulfilled, (state, action) => {
      if (!action.payload) return;

      const { tablesList, sections, bookingsByTable } = action.payload;
      state.tablesListForActiveService = tablesList;
      state.activeSections = sections;
      state.bookingsByTable = bookingsByTable;
    });
  },
});

async function getBookingsByTable(activeScheduleBookings: IBooking[]): Promise<IBookingsByTable> {
  if (!activeScheduleBookings.length) {
    return Promise.resolve({});
  }

  // not sure if we actually need this, but keeping it in just in case
  const bookingsWithNumberDuration: IBookingSubset[] = activeScheduleBookings.map((b) => {
    if (typeof b.duration === 'string') {
      b.duration = parseFloat(b.duration);
    }
    return b as IBookingSubset;
  });

  try {
    return BookingAvailabilityService.getBookingsByTable(bookingsWithNumberDuration);
  } catch (err) {
    console.warn(
      NS,
      `Error on 'getBookingsByTable' calling 'BookingAvailabilityService.getBookingsByTable' failed. schedule: `,
      bookingsWithNumberDuration,
      err
    );
    return Promise.resolve({});
  }
}

// Extract the action creators object and the reducer
const { actions, reducer } = activeSectionsSlice;
// Extract and export each action creator by name
export { actions as activeSectionsActions };
// Export the reducer, either as a default or named export
export default reducer;
